/* eslint-disable react/no-array-index-key */
import { useState, useEffect, useMemo } from 'react';
import _ from 'lodash';
import { t } from '@lingui/macro';

import Properties, { renderIpAddrList, renderPageLink, renderSiteList } from '../../properties/Properties';
import { useVirtualMachine, timeRangeFilterId, hypervisorFilterId, virtualMachineFilterId } from '../../filters';
import { getMsOf } from '../../../utilities/TimeRangeNavigationUtilities';
import { useCurrentFilterStateById } from '../../../stores/FilterStateStore';
import { useDataService } from '../../../services/ApiDataService';
import { stringProperty, linkProperty, listProperty, listLinkProperty } from '../../properties/funcs';
import { panelPropDefaults, panelPropTypes } from '../../page-framework/panel/PanelPropTypes';
import useStyles from '../../page-framework/panel/PanelStyles';
import Panel from '../../page-framework/panel/PanelDecorator';
import { getUpTimeDuration } from '../../../utilities/DateTimeUtilities';

const LOOPBACK_IP = '127.0.0.1';

const powerStates = {
    poweredOn: 'On',
    poweredOff: 'Off',
};

function usePropertiesColumns(data) {
    const hypervisorId = _.get(data, 'virtualization.parentUuid');
    return useMemo(() => {
        const filters = [{ name: hypervisorFilterId, uuid: hypervisorId }];
        return [
            {
                properties: [
                    stringProperty(t`Name`, 'name'),
                    stringProperty(t`Hostname`, 'virtualization.guestInfo.hostname'),
                    stringProperty(t`IP Address`, 'ipAddress'),
                    stringProperty(t`Virtual Type`, 'subType', () => t`Virtual Machine`),
                    stringProperty(t`Power State`, 'virtualization.powerState', renderPowerState),
                    stringProperty(t`Up Time`, 'virtualization.bootTime', getUpTimeDuration),
                    stringProperty(t`Vendor`, 'vendor'),
                    stringProperty(t`Technology`, 'virtualization.technology'),
                    stringProperty(t`vCenter`, 'virtualization.vCenter'),
                    stringProperty(t`Data Center`, 'virtualization.dataCenter'),
                    stringProperty(t`Resource Pool`, 'virtualization.resourcePool'),
                    linkProperty(
                        t`Hypervisor`,
                        'virtualization.hyperVisorName',
                        '/vmware-host-detail',
                        filters,
                        [virtualMachineFilterId],
                        renderPageLink
                    ),
                    listLinkProperty(t`Sites`, 'sites', [], [virtualMachineFilterId], renderSiteList),
                ],
            },
            {
                properties: [listProperty(t`IP Addresses`, 'ipAddresses', renderIpAddrList)],
            },
        ];
    }, [hypervisorId]);
}

/**
 * Convert the array of property name/value pairs into an object, then convert ipAddresses and loopbackIps from
 * pseudo-JSON strings into string arrays. Annotate any IPs in ipAddresses which are loopbacks, and remove the
 * localhost loopback from the list if present. Finally, include sites in the main set of properties.
 * @param data
 * @param sites
 * @returns {*}
 */
function buildPropertiesData(data, sites) {
    const properties = _(data).keyBy('name').mapValues('value').value();
    const loopbacks = decodeStringList(properties.loopbackIps);
    const ipAddresses = decodeStringList(properties.ipAddresses);
    const ipAddressesNoLoopback = _.pull(ipAddresses, LOOPBACK_IP);
    properties.ipAddresses = _.map(ipAddressesNoLoopback, (ip) => {
        return { ipAddress: ip, loopback: loopbacks.indexOf(ip) >= 0 };
    });
    properties.sites = sites;
    return properties;
}

function decodeStringList(str) {
    const list = str ? str.replace(/[[\]]/g, '') : null;
    if (!list) return [];
    return list.split(/,[ ]*/);
}

function renderPowerState(value) {
    return powerStates[value] || t`Unknown`;
}

/**
 * Combine data from serverinfo and device queries
 * @returns {Object}
 */
function usePropertiesData() {
    const vm = useVirtualMachine();
    const dataService = useDataService();
    const [props, setProps] = useState();
    const [timeRange] = useCurrentFilterStateById(timeRangeFilterId);
    const vmId = vm?.uuid;
    const start = timeRange?.start;
    const end = timeRange?.end;

    useEffect(() => {
        if (!vmId || !start) {
            return;
        }
        const serverInfoParams = {
            start: getMsOf(start),
            end: getMsOf(end),
            type: 'server',
            server: vmId,
        };
        const serverInfoRequest = dataService.get('/query/serverinfo', serverInfoParams);
        const deviceRequest = dataService.get(`/ngp/v2/devices/${vmId}`);

        Promise.all([serverInfoRequest, deviceRequest]).then((resolved) => {
            const [serverInfo, deviceData] = resolved;
            setProps(buildPropertiesData(serverInfo?.data, deviceData?.sites));
        });
    }, [dataService, vmId, start, end]);

    return props;
}

export function VirtualMachineProperties({ data }) {
    const classes = useStyles();
    const columns = usePropertiesColumns(data);
    return (
        <div className={classes.propertiesPanel}>
            {columns.map((def, index) => (
                <Properties propertiesDef={def} data={data} key={`column-${index}`} />
            ))}
        </div>
    );
}

export function VirtualMachinePropertiesPanel() {
    const properties = usePropertiesData();
    if (!properties) return null;
    return <VirtualMachineProperties data={properties} />;
}

VirtualMachinePropertiesPanel.propTypes = panelPropTypes;

VirtualMachinePropertiesPanel.defaultProps = {
    ...panelPropDefaults,
    title: t`Virtual Machine Properties`,
    autoHeight: true,
    hideActions: true,
    minContentHeight: 250,
};

export default Panel({
    filters: {
        required: [timeRangeFilterId, virtualMachineFilterId],
        optional: [],
    },
})(VirtualMachinePropertiesPanel);
